import { useEffect, useState } from "react";
import { api, queryString } from "../api.js";
import { ErrorBox, Pager, fmtRelative, fmtDate } from "../components/helpers.jsx";

const LIMIT = 50;

// Expanded detail — fetched on-demand when a row opens.
function SubjectDetail({ subject }) {
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get(`/api/admin/hwid-log/${encodeURIComponent(subject)}`)
            .then((d) => { if (alive) setRows(d.rows); })
            .catch((e) => { if (alive) setErr(e.message); });
        return () => { alive = false; };
    }, [subject]);

    if (err) return <div className="error">{err}</div>;
    if (!rows) return <div className="text-dim">Loading identifiers…</div>;
    if (rows.length === 0) return <div className="text-dim">No identifiers</div>;

    // Group identifiers by kind for the expanded panel so the user gets
    // a section per identifier type (bcd / smbios_uuid / mac / etc).
    const byKind = {};
    for (const r of rows) (byKind[r.kind] ??= []).push(r);

    return (
        <div style={{ padding: "8px 16px 12px", borderTop: "1px solid var(--border)" }}>
            {Object.entries(byKind).map(([kind, rs]) => (
                <div key={kind} style={{ marginTop: 8 }}>
                    <div style={{ fontWeight: 500, fontSize: 12, color: "var(--text-dim)", marginBottom: 4 }}>
                        {kind} <span style={{ color: "var(--text-muted)" }}>×{rs.length}</span>
                    </div>
                    <table className="data" style={{ marginBottom: 4 }}>
                        <tbody>
                            {rs.map((r) => (
                                <tr key={r.id}>
                                    <td className="mono" style={{ wordBreak: "break-all" }}>{r.value}</td>
                                    <td className="text-dim" style={{ width: 70 }}>{r.source}</td>
                                    <td className="text-dim" style={{ width: 110 }} title={fmtDate(r.firstSeen)}>
                                        {fmtRelative(r.firstSeen)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}

export default function HwidLog() {
    const [data, setData] = useState({ rows: [], total: 0 });
    const [q, setQ] = useState("");
    const [offset, setOffset] = useState(0);
    const [err, setErr] = useState(null);
    const [openSubject, setOpenSubject] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/api/admin/hwid-log" + queryString({ q, limit: LIMIT, offset }))
            .then((d) => { if (alive) setData(d); })
            .catch((e) => { if (alive) setErr(e.message); });
        return () => { alive = false; };
    }, [q, offset]);

    function toggle(subject) {
        setOpenSubject((cur) => (cur === subject ? null : subject));
    }

    return (
        <>
            <div className="page-header">
                <div>
                    <h1>HWID log</h1>
                    <div className="sub">
                        One row per subject. Click to expand and see every identifier the loader
                        has ever reported for that user / machine.
                    </div>
                </div>
            </div>

            <ErrorBox>{err}</ErrorBox>

            <div className="table-wrap">
                <div className="table-toolbar">
                    <input placeholder="Search subject, label, or any identifier value…" value={q}
                           onChange={(e) => { setOffset(0); setOpenSubject(null); setQ(e.target.value); }} />
                </div>
                <table className="data">
                    <thead>
                        <tr>
                            <th style={{ width: 24 }}></th>
                            <th>Subject</th>
                            <th>#</th>
                            <th>Kinds</th>
                            <th>Last seen</th>
                            <th>First seen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.length === 0 && (
                            <tr><td colSpan={6} className="text-dim" style={{ padding: 24, textAlign: "center" }}>
                                No entries
                            </td></tr>
                        )}
                        {data.rows.map((r) => {
                            const isOpen = openSubject === r.subject;
                            return (
                                <>
                                    <tr key={r.subject} onClick={() => toggle(r.subject)}
                                        style={{ cursor: "pointer" }}>
                                        <td className="text-dim" style={{ textAlign: "center" }}>
                                            {isOpen ? "▾" : "▸"}
                                        </td>
                                        <td>
                                            {r.subjectLabel && (
                                                <div style={{ fontWeight: 500 }}>{r.subjectLabel}</div>
                                            )}
                                            <div className="mono text-dim" style={{ fontSize: 11 }}>
                                                {r.subject}
                                            </div>
                                        </td>
                                        <td className="mono">{r.identifierCount}</td>
                                        <td className="text-dim" style={{ fontSize: 12 }}>
                                            {r.kinds.join(", ")}
                                        </td>
                                        <td title={fmtDate(r.lastSeen)}>{fmtRelative(r.lastSeen)}</td>
                                        <td className="text-dim" title={fmtDate(r.firstSeen)}>
                                            {fmtRelative(r.firstSeen)}
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr key={r.subject + ":detail"}>
                                            <td colSpan={6} style={{ background: "var(--bg)", padding: 0 }}>
                                                <SubjectDetail subject={r.subject} />
                                            </td>
                                        </tr>
                                    )}
                                </>
                            );
                        })}
                    </tbody>
                </table>
                <Pager total={data.total} limit={LIMIT} offset={offset} onChange={setOffset} />
            </div>
        </>
    );
}
